import { useState, type ChangeEvent, type MouseEvent } from "react";
import { Banner, PageState, Btn, Card, Chip, Icon, Input, Table, type TableColumn } from "@v0y4ge/jrni-ui";
import { T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { StatTile, FilterBar } from "../../shell/composites";
import { useViewMode, ViewToggle, useRowSelection, BulkActionBar, CARD_GRID, tabExport, eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "./eventSectionCounts";
import { useNav } from "../../shell/router";
import { useEvents } from "./useEvents";
import type { UIEvent } from "../../lib/events-adapter";
import { useT } from "../../lib/i18n";

/**
 * Archive — verbatim port of the prototype's EventsArchived (screens-subpages).
 * Sources the real ended events from the contract (`useEvents` → status "past"),
 * not fabricated rows. Read-only with reporting intact; clicking a row opens the
 * event detail drawer (Report). No Restore/Duplicate per F-80.
 */

function endOf(ev: UIEvent): Date | null {
  const raw = ev.raw as { endsAt?: string; end_utc?: string; startsAt?: string; start_utc?: string };
  const s = raw.endsAt ?? raw.end_utc ?? raw.startsAt ?? raw.start_utc;
  const d = s ? new Date(s) : null;
  return d && !isNaN(d.getTime()) ? d : null;
}

export default function EventsArchived() {
  const t = useT();
  const nav = useNav();
  const { events, loading } = useEvents();
  const [view, setView] = useViewMode("archived", "list");
  const [query, setQuery] = useState("");
  const [yearFilter, setYearFilter] = useState("all");

  const NOW = new Date();
  const relEnded = (ev: UIEvent): string => {
    const end = endOf(ev);
    if (!end) return "Ended";
    const days = Math.max(0, Math.round((NOW.getTime() - end.getTime()) / 86400000));
    if (days <= 0) return "Just ended";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 14) return "1 week ago";
    if (days < 60) return `${Math.round(days / 7)} weeks ago`;
    return ev.date;
  };

  const archived = events
    .filter((e) => e.status === "past")
    .slice()
    .sort((a, b) => (endOf(b)?.getTime() || 0) - (endOf(a)?.getTime() || 0))
    .map((e) => ({ ...e, archivedLabel: relEnded(e) }));

  const years = [...new Set(archived.map((e) => (e.date.match(/\d{4}/) || [""])[0]).filter(Boolean))].sort().reverse();
  const filtered = archived.filter((e) => {
    if (yearFilter !== "all" && !(e.date || "").includes(yearFilter)) return false;
    if (query.trim()) { const q = query.trim().toLowerCase(); if (!`${e.title} ${e.host}`.toLowerCase().includes(q)) return false; }
    return true;
  });
  const sel = useRowSelection(filtered.map((e) => e.id));
  const anyFilter = yearFilter !== "all" || !!query.trim();

  const tabItem = (id: string, label: string) => {
    const on = yearFilter === id;
    return <Chip key={id} onClick={() => setYearFilter(id)} selected={on}>{label}</Chip>;
  };

  type ArchivedRow = UIEvent & { archivedLabel: string };
  const columns: TableColumn<ArchivedRow>[] = [
    { id: "event", header: t("events.archived.event", "Event"), cell: (ev) => (
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <span style={{ width: 32, height: 32, borderRadius: 6, background: ev.hue, color: T.white, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, opacity: 0.7 }}>
          {ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}
        </span>
        <div style={{ color: T.n1, fontWeight: 600 }}>{ev.title}</div>
      </div>
    ) },
    { id: "date", header: t("events.archived.date", "Date"), cell: (ev) => <span style={{ color: T.n1 }}>{ev.date}</span> },
    { id: "host", header: t("events.archived.host", "Host"), cell: (ev) => <span style={{ color: T.n1 }}>{ev.host}</span> },
    { id: "registered", header: t("events.archived.registered", "Registered"), cell: (ev) => <span style={{ color: T.n1, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{ev.registered}</span> },
    { id: "archived", header: t("events.archived.archived", "Archived"), cell: (ev) => <span style={{ color: T.soft, fontSize: 12 }}>{ev.archivedLabel}</span> },
    { id: "actions", header: t("events.archived.actions", "Actions"), align: "right", minWidth: 160, cell: (ev) => (
      <span onClick={(e: MouseEvent<HTMLSpanElement>) => e.stopPropagation()} style={{ display: "inline-flex", gap: 4, justifyContent: "flex-end" }}>
        <Btn variant="ghost" size="sm" icon={<Icon.chart size={12} />} onClick={() => nav.openEvent(ev)}>{t("events.archived.report", "Report")}</Btn>
      </span>
    ) },
  ];

  return (
    <Screen rail="events" tabs={eventsSectionTabs(nav, "archived", useEventSectionCounts())} activeTab="archived">
      <PageHeader
        title={t("events.archived.archive", "Archive")}
        subtitle={t("events.archived.archivedLengthArchivedEventArchivedLength1SReadOnlyWithReporting.icu", { count: archived.length })}
        actions={<Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => tabExport("archived events", archived as unknown as Record<string, unknown>[])}>{t("common.actions.export", "Export")}</Btn>}
      />
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t("events.archived.archivedEvents", "Archived events")} value={archived.length} icon={<Icon.archive size={14} />} accent={T.p1} />
        <StatTile label={t("events.archived.registeredAllTime", "Registered (all-time)")} value={archived.reduce((s, e) => s + (e.registered || 0), 0).toLocaleString()} icon={<Icon.users size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("events.archived.hosts", "Hosts")} value={[...new Set(archived.map((e) => e.host))].length} icon={<Icon.user size={14} />} accent={T.green1} />
        <StatTile label={t("events.archived.reportingRetained", "Reporting retained")} value="100%" icon={<Icon.chart size={14} />} accent={T.orange1} />
      </div>
      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} size="sm" placeholder={t("events.archived.searchArchivedEvents", "Search archived events…")} value={query} onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)} style={{ width: 280 }} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        {tabItem("all", `All · ${archived.length}`)}
        {years.map((y) => tabItem(y, y))}
        {anyFilter ? (
          <Btn variant="ghost" size="sm" onClick={() => { setQuery(""); setYearFilter("all"); }}>{t("events.archived.clearAll", "Clear all")}</Btn>
        ) : null}
        <span style={{ marginLeft: "auto" }} />
        <ViewToggle mode={view} onChange={setView} />
      </FilterBar>

      <BulkActionBar count={sel.selected.size} noun="event" onClear={sel.clear} actions={[
        { label: t("common.actions.export", "Export"), icon: <Icon.download size={12} />, onClick: () => { tabExport("archived events", archived.filter((x) => sel.selected.has(x.id)) as unknown as Record<string, unknown>[]); sel.clear(); } },
      ]} />

      <div style={{ padding: "0 24px" }}>
        <Banner tone="info">
          <b>{t("events.archived.archive", "Archive")}</b> {t("events.archived.holdsPastAndRetiredEventsReadOnlyWithReportingIntactOpenOne", "holds past and retired events — read-only, with reporting intact. Open one to view its report; its registrations and metrics stay available.")}</Banner>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {loading || filtered.length === 0 ? (
          <PageState skeleton={view === "grid" ? "cards" : "table"} noun={t("events.sectionState.nounArchivedEvents", "archived events")} icon={<Icon.archive size={20} />}
            loading={loading} empty={filtered.length === 0} filtered={!!anyFilter}
            onClearFilters={() => { setQuery(""); setYearFilter("all"); }}
            emptyBody={t("events.archived.pastEventsLandHereAutomaticallyWithTheirReportingIntact", "Past events land here automatically with their reporting intact.")} />
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16 }}>
            {filtered.map((ev) => (
              <Card key={ev.id} style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ width: 40, height: 40, borderRadius: 8, background: ev.hue, color: T.white, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, opacity: 0.7, flexShrink: 0 }}>
                    {ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.title}</div>
                    <div style={{ fontSize: 12, color: T.soft }}>{ev.date} · {ev.host}</div>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.soft, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
                  <span><strong style={{ color: T.n1 }}>{ev.registered}</strong> {t("events.archived.registeredb1a9e561", "registered")}</span>
                  <span>{t("events.archived.archived", "Archived")}{" "}{ev.archivedLabel}</span>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <Btn variant="ghost" size="sm" icon={<Icon.chart size={12} />} onClick={() => nav.openEvent(ev)}>{t("events.archived.report", "Report")}</Btn>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card style={{ padding: 0 }}>
            <Table<ArchivedRow>
              columns={columns}
              rows={filtered}
              getRowId={(ev) => ev.id}
              selectedIds={sel.selected}
              onRowSelect={(id) => sel.toggle(id)}
              onSelectAll={() => sel.toggleAll()}
              onRowClick={(ev) => nav.openEvent(ev)}
              style={{ border: "none", borderRadius: 0 }}
            />
          </Card>
        )}
      </div>
    </Screen>
  );
}
